import {
  ButtonSelectChoice,
  GenderNumber,
  PageTextNumQuestion,
  RawScenario,
  RawScenarioPageInfo,
  RawScenarioPageInfoAddress, RawScenarioPageInfoAgreement,
  RawScenarioPageInfoButtonSelect,
  RawScenarioPageInfoEmail,
  RawScenarioPageInfoGenderAndBirthDate,
  RawScenarioPageInfoInputSubQuery, RawScenarioPageInfoMainComplaint,
  RawScenarioPageInfoMessage,
  RawScenarioPageInfoNameKana,
  RawScenarioPageInfoNumText,
  RawScenarioPageInfoSelectSubQuery,
  RawScenarioPageInfoTel, RawScenarioPageInfoWithTermsJump,
} from '../types/rawScenario';
import { Scenario, ScenarioItem, SkipCondition } from '../stores/scenarioState';
import { getTextTranslateRule, } from '../components/items/TranslateRules';
import { ScenarioComponent, TextArea, TextInput } from '../types/components';
import {
  toMessageComponentId,
  toMessageItemId,
  toOkButtonComponentId,
  toOrderedComponentId,
  toTitleItemId
} from "./idMapping";
import { Questions } from '../api/getQuestions';
import { Color, convert } from '../types/colors';
import { jsonArray, jsonValue } from '../types/jsonValue';
import { formatDate } from './formatDate';

export type AdditionalInfo = {
  icon: string;
  title: string;
  color: Color;
}

export const translateQuestionsToScenario = (questions: Questions): Scenario => {
  const meta = {
    header: {
      title: '問診票',
    },
    balloon: {
      icon: questions.icon,
    },
    body: {
      color: convert(questions.color)
    }
  } as const;

  if (questions.questions.length === 0) {
    return {
      ...meta,
      items: [
        {
          id: '--not-found-message',
          type: 'opposite',
          components: [
            component({
              id: '--not-found-message',
              type: 'message',
              message: '現在ご回答いただける問診票がありません。'
            })
          ],
          skip: []
        }
      ]
    }
  }

  if (questions.questions.length === 1) {
    return {
      ...meta,
      items: []
    }
  }

  const id = '--medical-questionnaire';
  return {
    ...meta,
    items: [
      {
        id: toTitleItemId(id),
        type: 'opposite',
        components: [{
          type: 'message',
          id: toTitleItemId(id),
          message: 'あてはまる問診票を選んでください。'
        }],
        skip: []
      },
      {
        id: '--medical-questionnaire',
        type: 'self',
        components: [
          component({
            id: '--medical-questionnaire-select',
            type: 'medical-questionnaire-select',
            options: questions.questions.map(q => ({
              questionId: q.questionId,
              label: q.dispName,
            })),
          })
        ],
        skip: []
      }
    ]
  }
}

export const translateRawToScenarioItems = (raw: RawScenario, preview: boolean = false): ScenarioItem[] => {
  return [...buildItems(raw, !preview && raw.personalInfo == null), ...buildCompleteMessage()];
}


// かな氏名・生年月日・性別・電話番号・メールアドレスのうち、テンプレートに存在すればデフォルト値として利用
// 個人情報を聞く場合は現在日を予約日のデフォルトとして利用
export const translateRawToDefaultForm = (raw: RawScenario): { [id: string]: jsonValue | jsonArray } => {
  if (raw.personalInfo == null || !('patientId' in raw.personalInfo)) {
    return {
      '--personal-info:reserve-date': formatDate(new Date()),
    };
  }

  const form: { [id: string]: jsonValue | jsonArray } = {};

  // personalInfo がある場合はかな氏名は必須
  const kanaItem = getTargetItem(raw, 'かな氏名');
  if (kanaItem) {
    form[kanaItem.components.filter(c => c.type === 'text')[0].id] = `${raw.personalInfo.fname1 ?? ''}　${raw.personalInfo.fname2 ?? ''}`.trim();
  }

  if (raw.personalInfo.birthday) {
    const birthDateItem = getTargetItem(raw, '生年月日');
    if (birthDateItem) {
      form[birthDateItem.components.filter(c => c.type === 'date')[0].id] = raw.personalInfo.birthday;
    }
  }

  if (raw.personalInfo.sex) {
    const sexItem = getTargetItem(raw, '生年月日');
    if (sexItem) {
      form[sexItem.components.filter(c => c.type === 'select')[0].id] = raw.personalInfo.sex === 1 ? '男' : '女';
    }
  }

  if (raw.personalInfo.tel) {
    const telItem = getTargetItem(raw, '電話番号');
    if (telItem) {
      form[telItem.components.filter(c => c.type === 'text')[0].id] = raw.personalInfo.tel;
    }
  }

  if (raw.personalInfo.mailAddress) {
    const mailItem = getTargetItem(raw, 'メール');
    if (mailItem) {
      form[mailItem.components.filter(c => c.type === 'text')[0].id] = raw.personalInfo.mailAddress;
    }
  }

  return form;
}

function buildItems(raw: RawScenario, withPersonalInputItems: boolean): ScenarioItem[] {
  const personalInfoInputItems = withPersonalInputItems ? buildPersonalInfoInputItems(raw) : [];
  const rawItems = raw.scenarioPageInfo;
  const items = rawItems
    .filter(rawItem => {
      return isDisplayableSetting(rawItem) &&
        isDisplayableAge(raw.personalInfo?.age, rawItem) &&
        isDisplayableSex(raw.personalInfo?.sex, rawItem);
    })
    .filter(rawItem => personalInfoInputItems.length === 0 || rawItem.template !== 'かな氏名')
    .flatMap(rawItem => buildItem(rawItem, raw));
  return [...personalInfoInputItems, ...items];
}

function buildPersonalInfoInputItems(raw: RawScenario): ScenarioItem[] {
  if (raw.personalInfo != null) return [];

  const id = '--personal-info';
  const reserveDateId = `${id}:reserve-date`;
  const fnameId = `${id}:fname`;
  const min = new Date();
  const max = new Date(min.getFullYear(), min.getMonth() + 7, 0);
  return [
    {
      id: toTitleItemId(id),
      type: 'opposite',
      components: [{
        type: 'message',
        id: toTitleItemId(id),
        message: '以下の情報を入力してください。'
      }],
      skip: []
    },
    {
      id: id,
      type: 'self',
      components: [
        component({
          id: reserveDateId,
          type: 'date',
          name: 'reserve-date',
          message: '予約日 (Appointment date)',
          min: formatDate(min),
          max: formatDate(max),
          initValue: formatDate(min),
          optional: false,
        }),
        component({
          id: fnameId,
          type: 'text',
          name: 'name',
          size: 25,
          translateRule: 'kana',
          message: 'かな氏名 (Enter your name in Hiragana)',
          optional: false,
        }),
        component({
          id: toMessageComponentId(fnameId, 0),
          type: 'message',
          message: '※入力例（やまだ＿たろう） 姓と名の間に空白を入れてください',
          size: 'small'
        }),
        component({
          id: toOkButtonComponentId(id),
          type: 'ok',
          label: '→ すすむ',
          watchingIds: [reserveDateId, fnameId],
        })
      ],
      skip: [],
    }
  ]
}

function buildItem(rawItem: RawScenarioPageInfo, raw: RawScenario) {
  const rawItems = raw.scenarioPageInfo;
  switch (rawItem.template) {
    case '案内ページ':
      return buildMessageComponent(rawItem, rawItems);
    case '案内ページ(画像付き)':
      return [];
    case 'ボタン選択':
      return buildSelectComponent(rawItem, rawItems);
    case 'ボタン選択(画像有)':
      return buildSelectComponent(rawItem, rawItems);
    case 'ボタン選択(画像説明付き)':
      return [];
    case 'ボタン選択(動画説明付き)':
      return [];
    case '数字、文字入力':
      return buildNumTextComponent(rawItem, rawItems);
    case 'ページ内副問い合わせ(ボタン選択)':
      return buildSelectSubqueryComponent(rawItem, rawItems);
    case 'ページ内副問い合わせ(入力)':
      return buildInputSubqueryComponent(rawItem, rawItems);
    case 'サイン':
      return [];
    case 'シェーマ':
      return [];
    case '住所入力':
      return buildInputAddressComponent(rawItem, rawItems);
    case 'メール':
      return buildEmailComponent(rawItem, rawItems);
    case 'かな氏名':
      return buildNameKanaComponent(rawItem, rawItems);
    case '生年月日':
      return buildGenderAndBirthDateComponent(rawItem, rawItems);
    case '電話番号':
      return buildTelComponent(rawItem, rawItems);
    case '主訴':
      return buildSelectComponent(rawItem, rawItems);
    case '一覧ページ':
      return [];
    case '同意ページ':
      return buildAgreementComponent(rawItem, rawItems);
    default:
      console.warn(`unknown template ${(rawItem as any).template}`, rawItem);
      return [];
  }
}

function isDisplayableSetting(item: RawScenarioPageInfo) {
  const detail = item.detail;
  if (item.web_disp === 'no') return false;
  if (detail != null && 'page_disp' in detail && detail.page_disp === 'no') return false;
  return true;
}

function isDisplayableAge(age: number | null | undefined, item: RawScenarioPageInfo) {
  if (age == null) return true;
  if (item.page_disp_agejudg === 'no') return true;
  const from = parseInt(item.page_disp_agefr);
  const to = ['', '0'].includes(item.page_disp_ageto) ? Infinity : parseInt(item.page_disp_ageto);
  return from <= age && age <= to;
}

function isDisplayableSex(sex: GenderNumber | null | undefined, item: RawScenarioPageInfo) {
  if (sex == null) return true;
  if (item.page_disp_sexjudg === 'no') return true;
  return item.page_disp_sex === String(sex);
}

function buildCompleteMessage(): ScenarioItem[] {
  return [
    {
      id: "--finish:message",
      type: "opposite",
      components: [
        {
          id: "--finish:message:0",
          type: "message",
          message: "以上で質問は終了です。\n以下のボタンを押して、内容を送信してください。",
        }
      ],
      skip: [],
    },
    {
      id: "--finish:button",
      type: "self",
      components: [
        {
          id: "--finish:button:0",
          type: "complete",
        }
      ],
      skip: [],
    },
    {
      id: "--complete:message",
      type: "opposite",
      components: [
        {
          id: "--complete:message:0",
          type: "message",
          message: "ありがとうございました。",
        }
      ],
      skip: [],
    },
  ]
}

function buildMessageComponent(rawItem: RawScenarioPageInfoMessage, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  return [
    ...buildTitle(rawItem),
    ...buildMessage(rawItem, rawItem.detail.message),
  ];
}
function buildSelectComponent(rawItem: RawScenarioPageInfoButtonSelect | RawScenarioPageInfoMainComplaint, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  if (rawItem.detail.page_disp === 'no') return [];
  return [
    ...buildTitle(rawItem),
    ...buildMessage(rawItem, rawItem.detail.question),
    {
      id: rawItem.page_id,
      type: 'self',
      components:
        rawItem.detail.selection === 'multi' ? [
          {
            type: 'multiselect',
            id: rawItem.page_id,
            options: rawItem.detail.choices.map(buildOption),
            optional: rawItem.detail.input_required === 'no'
          },
          buildOkButton(rawItem, [rawItem.page_id]),
        ] : [
          {
            type: 'select',
            id: rawItem.page_id,
            options: rawItem.detail.input_required === 'no'
              ? [...rawItem.detail.choices.map(buildOption), {value: '', label: 'スキップ'}]
              : rawItem.detail.choices.map(buildOption),
            optional: rawItem.detail.input_required === 'no'
          }
        ],
      skip: buildSkip(rawItem.page_id, rawItem, rawItems)
    }
  ]
}


function buildNumTextComponent(rawItem: RawScenarioPageInfoNumText, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  const components: ScenarioComponent[] = rawItem.detail.input_question
    .filter(q => q.page_disp === 'yes')
    .map(buildTextInput);
  components.push(buildOkButton(rawItem, components.map(c => c.id)));

  return [
    ...buildTitle(rawItem),
    {
      id: rawItem.page_id,
      type: 'self',
      components,
      skip: []
    }
  ]
}
function buildSelectSubqueryComponent(rawItem: RawScenarioPageInfoSelectSubQuery, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  if (rawItem.detail.btn_select_questions.every(q => q.page_disp === 'no')) return [];
  const components: ScenarioComponent[] = rawItem.detail.btn_select_questions.flatMap((q, i) => [
      ...(q.page_disp === 'yes' ? [
        ...(q.question ? [
          component({
            id: toMessageComponentId(rawItem.page_id, i),
            type: 'message',
            message: q.question
          })
        ] : []),
        component({
          id: toOrderedComponentId(rawItem.page_id, i),
          type: q.selection === 'multi' ? 'multiselect' : 'select',
          options: q.choices.map(buildOption),
          optional: q.input_required === 'no'
        })
      ] : [])
    ]);
  components.push(buildOkButton(rawItem, components.map(c => c.id)))

  return [
    ...buildTitle(rawItem),
    {
      id: rawItem.page_id,
      type: 'self',
      components: components,
      skip: buildSkip(toOrderedComponentId(rawItem.page_id, 0), rawItem, rawItems),
    }
  ];
}
function buildInputSubqueryComponent(rawItem: RawScenarioPageInfoInputSubQuery, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  const inputInvisible = rawItem.detail.input_question.questions.every(q => q.page_disp === 'no')
  const selectInvisible = rawItem.detail.btn_select_question.page_disp === 'no';
  if (inputInvisible && selectInvisible) return [];

  const select = rawItem.detail.btn_select_question;

  let messageIndex = 0;
  const components: ScenarioComponent[] = [
    ...(!selectInvisible ? [
      ...(select.question ? [
        component({
          id: toMessageComponentId(rawItem.page_id, messageIndex++),
          type: 'message',
          message: select.question
        })
      ] : []),
      component({
        id: toOrderedComponentId(rawItem.page_id, 0),
        type: select.selection === 'multi' ? 'multiselect' : 'select',
        options: select.choices.map(buildOption),
        optional: select.input_required === 'no',
      })
    ] : []),
    ...rawItem.detail.input_question.questions.flatMap((q) => [
      ...(q.page_disp === 'yes' ? [
        buildTextInput(q),
      ] : [])
    ])
  ];

  components.push(buildOkButton(rawItem, components.map(c => c.id)))

  return [
    ...buildTitle(rawItem),
    {
      id: rawItem.page_id,
      type: 'self',
      components: components,
      skip: buildSkip(toOrderedComponentId(rawItem.page_id, 0), rawItem, rawItems),
    }
  ]
}
function buildInputAddressComponent(rawItem: RawScenarioPageInfoAddress, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  return [
    ...buildTitle(rawItem),
    {
      id: rawItem.page_id,
      type: 'self',
      components: [
        component({
          id: toOrderedComponentId(rawItem.page_id, 0),
          type: 'text',
          name: 'postal-code',
          autoComplete: 'postal-code',
          size: 8,
          maxLength: 8,
          minLength: 8,
          translateRule: 'postal-code',
          placeholder: '000-0000',
          message: rawItem.detail.post_code_question?.question,
          prefix: '〒',
          optional: true,
          optionalTextHidden: true,
        }),
        component({
          id: toOrderedComponentId(rawItem.page_id, 1),
          postalCodeComponentId: toOrderedComponentId(rawItem.page_id, 0),
          type: 'address',
          name: 'address-line1',
          autoComplete: 'address-level2',
          message: rawItem.detail.address_question1?.question,
          optional: true,
          optionalTextHidden: true,
        }),
        component({
          id: toOrderedComponentId(rawItem.page_id, 2),
          type: 'text',
          name: 'address-line2',
          autoComplete: 'street-address',
          size: 25,
          translateRule: 'text',
          message: rawItem.detail.address_question2?.question,
          optional: true,
          optionalTextHidden: true,
        }),
        buildOkButton(rawItem, [rawItem.page_id]),
      ],
      skip: []
    }
  ]
}
function buildEmailComponent(rawItem: RawScenarioPageInfoEmail, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  return [
    ...buildTitle(rawItem),
    {
      id: rawItem.page_id,
      type: 'self',
      components: [
        component({
          id: rawItem.page_id,
          type: 'text',
          name: 'email',
          autoComplete: 'email',
          size: 25,
          translateRule: 'email',
          message: rawItem.detail.question,
          optional: true,
        }),
        buildOkButton(rawItem, [rawItem.page_id]),
      ],
      skip: []
    }
  ]
}
function buildNameKanaComponent(rawItem: RawScenarioPageInfoNameKana, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  return [
    ...buildTitle(rawItem),
    {
      id: rawItem.page_id,
      type: 'self',
      components: [
        component({
          id: rawItem.page_id,
          type: 'text',
          name: 'name',
          size: 25,
          translateRule: 'kana',
          message: rawItem.detail.question,
          optional: false,
        }),
        buildOkButton(rawItem, [rawItem.page_id]),
      ],
      skip: []
    }
  ];
}
function buildGenderAndBirthDateComponent(rawItem: RawScenarioPageInfoGenderAndBirthDate, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  const now = new Date();
  return [
    ...buildTitle(rawItem),
    {
      id: rawItem.page_id,
      type: 'self',
      components: [
        component({
          id: toOrderedComponentId(rawItem.page_id, 0),
          type: 'select',
          options: rawItem.detail.gender_question.choices.map(buildOption),
          optional: false
        }),
        component({
          id: toOrderedComponentId(rawItem.page_id, 1),
          type: 'date',
          name: 'birthday',
          message: rawItem.detail.era_question.question,
          min: '1912-07-30',
          max: formatDate(now),
          initValue: formatDate(new Date(now.getFullYear() - 20, now.getMonth(), now.getDate())),
          autoComplete: 'bday',
          optional: false
        }),
        buildOkButton(rawItem, [rawItem.page_id]),
      ],
      skip: buildSkip(toOrderedComponentId(rawItem.page_id, 0), rawItem, rawItems),
    }
  ]
}
function buildTelComponent(rawItem: RawScenarioPageInfoTel, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  const components: ScenarioComponent[] = rawItem.detail.tel_question
    .filter(q => q.page_disp === 'yes')
    .map((q, i) => component({
      id: toOrderedComponentId(rawItem.page_id, i),
      type: 'text',
      name: `tel${i === 0 ? '' : i + 1}`,
      autoComplete: 'tel',
      size: 17,
      translateRule: 'phone-number',
      message: q.question,
      optional: true,
      optionalTextHidden: true,
    }));
  components.push(buildOkButton(rawItem, components.map(c => c.id)))
  return [
    ...buildTitle(rawItem),
    {
      id: rawItem.page_id,
      type: 'self',
      components: components,
      skip: []
    }
  ];
}
function buildAgreementComponent(rawItem: RawScenarioPageInfoAgreement, rawItems: RawScenarioPageInfo[]): ScenarioItem[] {
  const components: ScenarioComponent[] = [
    component({
      id: toOrderedComponentId(rawItem.page_id, 0),
      type: 'message',
      message: rawItem.detail.summary.replace(/<br[^>]*>/g, '\n'),
    }),
    component({
      id: toOrderedComponentId(rawItem.page_id, 1),
      type: 'scroll-box',
      content: rawItem.detail.detail.replace(/<br[^>]*>/g, '\n'),
    }),
    component({
      id: toOrderedComponentId(rawItem.page_id, 2),
      type: 'select',
      options: rawItem.detail.input_required === 'no'
        ? [...rawItem.detail.choices.map(buildOption), {value: '', label: 'スキップ'}]
        : rawItem.detail.choices.map(buildOption),
      optional: false
    })
  ];

  return [
    ...buildTitle(rawItem),
    {
      id: rawItem.page_id,
      type: 'self',
      components,
      skip: []
    }
  ];
}

function buildSkip(componentId: string, rawItem: RawScenarioPageInfoWithTermsJump, source: RawScenarioPageInfo[]): SkipCondition[] {
  const restItems = source.slice(source.indexOf(rawItem) + 1);
  const termJumpValues = rawItem.terms_jump.map(j => j.decision);
  const options = getChoices(rawItem).map(buildOption)
    .filter(o => !termJumpValues.includes(o.value));

  rawItem.terms_jump.map(j => j.jump)

  return [
    ...rawItem.terms_jump.map<SkipCondition>(j => ({
      condition: {[componentId]: j.decision},
      skipScenarioItemIds: getItemIdsUntilPresent(restItems, j.jump)
    })),
    ...(rawItem.not_terms_jump === '' ? [] : options.map(o => ({
      condition: {[componentId]: o.value},
      skipScenarioItemIds: getItemIdsUntilPresent(restItems, rawItem.not_terms_jump)
    }))),
    ...('input_required' in rawItem.detail && rawItem.detail.input_required === 'yes' ? [] : [{
      condition: {[componentId]: ''},
      skipScenarioItemIds: getItemIdsUntilPresent(restItems, rawItem.not_terms_jump)
    }]),
  ];

}

function getChoices(rawItem: RawScenarioPageInfoWithTermsJump) {
  switch (rawItem.template) {
    case 'ボタン選択':
    case 'ボタン選択(画像有)':
    case 'ボタン選択(画像説明付き)':
    case 'ボタン選択(動画説明付き)':
    case '主訴':
      return rawItem.detail.choices;
    case 'ページ内副問い合わせ(ボタン選択)':
      return rawItem.detail.btn_select_questions[0].choices;
    case 'ページ内副問い合わせ(入力)':
      return rawItem.detail.btn_select_question.choices;
    case '生年月日':
      return rawItem.detail.gender_question.choices;
  }
}

function getItemIdsUntilPresent(pages: RawScenarioPageInfo[], pageId: string): string[] {
  const index = pages.findIndex(p => p.page_id === pageId)
  if (index < 0) return [];
  return pages.slice(0, index)
    .flatMap(p => [p.page_id, toTitleItemId(p.page_id), toMessageItemId(p.page_id)]);
}


function buildTitle(page: RawScenarioPageInfo): ScenarioItem[] {
  let title = page.title2;
  if (page.template === '同意ページ') {
    title = `${page.detail.title} (${page.detail.agree_level}${page.detail.agree_branch})`;
  }
  if (!title) {
    return [];
  }
  return [{
    id: toTitleItemId(page.page_id),
    type: 'opposite',
    components: [{
      type: 'message',
      id: toTitleItemId(page.page_id),
      message: title,
    }],
    skip: []
  }];
}
function buildMessage(page: RawScenarioPageInfo, message: string, skip: SkipCondition[] = []): ScenarioItem[] {
  if (!message) return [];
  return [{
    id: toMessageItemId(page.page_id),
    type: 'opposite',
    components: [{
      type: 'message',
      id: toMessageComponentId(page.page_id, 0),
      message: message.replace(/<br[^>]*>/g, '\n'),
    }],
    skip
  }];
}
function buildOkButton(page: RawScenarioPageInfo, watchingIds: string[]): ScenarioComponent {
  return component({
    type: 'ok',
    id: toOkButtonComponentId(page.page_id),
    label: '→ すすむ',
    watchingIds: watchingIds
  });
}

function buildTextInput(q: PageTextNumQuestion): TextInput | TextArea {
  const line = parseInt(q.input_line);
  if (line > 1) {
    return {
      type: 'textarea',
      id: q.item_id,
      message: q.question,
      name: q.item_id,
      translateRule: getTextTranslateRule(q.type_detail),
      rows: line,
      cols: q.disp_len !== '' ? parseInt(q.disp_len) : undefined,
      optional: q.input_required === 'no',
      placeholder: q.default_value !== '' ? q.default_value : undefined,
      prefix: q.prefix !== '' ? q.prefix : undefined,
      suffix: q.suffix !== '' ? q.suffix : undefined,
    }
  }

  return {
    type: 'text',
    id: q.item_id,
    message: q.question,
    name: q.item_id,
    translateRule: getTextTranslateRule(q.type_detail),
    size: q.disp_len !== '' ? parseInt(q.disp_len) : undefined,
    maxLength: q.input_len !== '' ? parseInt(q.input_len) : undefined,
    max: q.input_max !== '' ? parseInt(q.input_max) : undefined,
    min: q.input_min !== '' ? parseInt(q.input_min) : undefined,
    optional: q.input_required === 'no',
    placeholder: q.default_value !== '' ? q.default_value : undefined,
    prefix: q.prefix !== '' ? q.prefix : undefined,
    suffix: q.suffix !== '' ? q.suffix : undefined,
  }
}

function buildOption(c: ButtonSelectChoice) {
  return {value: c.disp_name, label: c.disp_name};
}

const component = <T extends ScenarioComponent>(data: T): T => data;


function getTargetItem(raw: RawScenario, template: RawScenarioPageInfo['template']) {
  const pageInfo = raw.scenarioPageInfo.find(p => p.template === template);
  if (pageInfo == null) return null;
  return buildItem(pageInfo, raw).find(i => i.id === pageInfo.page_id) ?? null;
}
